import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { Search } from '@mui/icons-material';

const SearchComponent = ({ 
  placeholder = "Поиск товаров Zara...",
  buttonText = "Найти",
  showTitle = true,
  variant = "default", // default, compact, hero
  onSearch = null,
  showGenderSelector = true,
  initialGender = 'WOMAN'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGender, setSelectedGender] = useState(initialGender);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Введите поисковый запрос');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const query = searchQuery.trim();
      //console.log(`🔍 SearchComponent: выполняется поиск по запросу "${query}" с полом "${selectedGender}"`);
      
      // Если передан onSearch, вызываем его
      if (onSearch) {
        //console.log(`🔍 SearchComponent: вызываем onSearch с запросом "${query}" и полом "${selectedGender}"`);
        onSearch(query, selectedGender);
        setSearchQuery('');
        return;
      }
      
      // Иначе переходим на страницу результатов поиска
      //console.log(`🔍 SearchComponent: переход на страницу поиска с запросом "${query}" и полом "${selectedGender}"`);
      const encodedQuery = encodeURIComponent(query);
      const encodedGender = encodeURIComponent(selectedGender);
      navigate(`/search?q=${encodedQuery}&gender=${encodedGender}`);
      
    } catch (err) {
      console.error('❌ Ошибка поиска:', err);
      setError('Ошибка при выполнении поиска');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleGenderChange = (gender) => {
    if (gender !== selectedGender) {
      //console.log(`🔄 Смена пола с ${selectedGender} на ${gender}`);
      setSelectedGender(gender);
      
      // Если есть поисковый запрос, сразу выполняем новый поиск с новым полом
      if (searchQuery.trim()) {
        //console.log(`🔄 Выполняем поиск "${searchQuery}" с новым полом ${gender}`);
        handleSearchWithGender(searchQuery, gender);
      }
    }
  };

  // Обновляем selectedGender при изменении initialGender
  useEffect(() => {
    if (initialGender !== selectedGender) {
      //console.log(`🔄 Обновляем пол с ${selectedGender} на ${initialGender}`);
      setSelectedGender(initialGender);
    }
  }, [initialGender, selectedGender]);

  const handleSearchWithGender = (query, gender) => {
    try {
      //console.log(`🔍 SearchComponent: выполняется поиск по запросу "${query}" с полом "${gender}"`);
      
      // Если передан onSearch, вызываем его
      if (onSearch) {
        //console.log(`🔍 SearchComponent: вызываем onSearch с запросом "${query}" и полом "${gender}"`);
        onSearch(query, gender);
        setSearchQuery('');
        return;
      }
      
      // Иначе переходим на страницу результатов поиска
      //console.log(`🔍 SearchComponent: переход на страницу поиска с запросом "${query}" и полом "${gender}"`);
      const encodedQuery = encodeURIComponent(query);
      const encodedGender = encodeURIComponent(gender);
      navigate(`/search?q=${encodedQuery}&gender=${encodedGender}`);
      
    } catch (err) {
      console.error('❌ Ошибка поиска:', err);
      setError('Ошибка при выполнении поиска');
    }
  };

  // Обновляем selectedGender при изменении initialGender
  useEffect(() => {
    setSelectedGender(initialGender);
  }, [initialGender]);

  const renderGenderSelector = () => {
    if (!showGenderSelector) return null;
    
    const genders = [
      { value: 'WOMAN', label: 'Женщины' },
      { value: 'MAN', label: 'Мужчины' },
      { value: 'KID', label: 'Дети' }
    ];
    
    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {genders.map((gender) => (
          <Chip
            key={gender.value}
            label={gender.label}
            onClick={() => handleGenderChange(gender.value)}
            variant={selectedGender === gender.value ? 'filled' : 'outlined'}
            sx={{
              backgroundColor: selectedGender === gender.value ? 'primary.main' : 'transparent',
              color: selectedGender === gender.value ? 'white' : 'primary.main',
              borderColor: 'primary.main',
              borderWidth: '2px',
              fontWeight: selectedGender === gender.value ? 600 : 400,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: selectedGender === gender.value ? 'primary.main' : 'primary.light',
                color: 'white',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }
            }}
          />
        ))}
      </Box>
    );
  };

  // Разные варианты отображения
  if (variant === 'compact') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Поле ввода и кнопка на одной строке */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <TextField
            size="small"
            placeholder={placeholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            variant="outlined"
            sx={{ 
              minWidth: { xs: '100%', sm: 200 },
              flex: { xs: 'none', sm: 1 }
            }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSearch}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <Search />}
            sx={{ 
              textTransform: 'none',
              minWidth: { xs: '100%', sm: 'auto' }
            }}
          >
            {loading ? 'Поиск...' : buttonText}
          </Button>
        </Box>
        
        {/* Выбор пола под полем ввода */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: { xs: 'center', sm: 'flex-start' }
        }}>
          {renderGenderSelector()}
        </Box>
      </Box>
    );
  }

  if (variant === 'hero') {
    return (
      <Paper elevation={3} sx={{ p: 4, maxWidth: 700, mx: 'auto', backgroundColor: 'white' }}>
        {showTitle && (
          <Typography variant="h6" sx={{ mb: 3, color: 'black', fontWeight: 500, textAlign: 'center' }}>
            Поиск товаров
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {/* Поле ввода и кнопка на одной строке */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <TextField
              fullWidth
              label="Поисковый запрос"
              placeholder={placeholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              variant="outlined"
              size="large"
              sx={{ 
                flex: { xs: 'none', sm: 1 }, 
                minWidth: { xs: '100%', sm: 300 }
              }}
            />
            <Button
              variant="contained"
              size="large"
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Search />}
              sx={{ 
                px: 4, 
                py: 1.5, 
                textTransform: 'none',
                backgroundColor: 'black',
                minWidth: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  backgroundColor: '#333'
                }
              }}
            >
              {loading ? 'Поиск...' : buttonText}
            </Button>
          </Box>
          
          {/* Выбор пола под полем ввода */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: { xs: 'center', sm: 'flex-start' }
          }}>
            {renderGenderSelector()}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          💡 Введите название товара, категорию или описание
        </Typography>
      </Paper>
    );
  }

  // По умолчанию - простой вариант
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Поле ввода и кнопка на одной строке */}
      <Box sx={{ 
        display: 'flex', 
        gap: 1, 
        alignItems: 'center',
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <TextField
          fullWidth
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          variant="outlined"
          size="medium"
          sx={{ 
            flex: { xs: 'none', sm: 1 }, 
            minWidth: { xs: '100%', sm: 200 }
          }}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <Search />}
          sx={{ 
            textTransform: 'none',
            minWidth: { xs: '100%', sm: 'auto' }
          }}
        >
          {loading ? 'Поиск...' : buttonText}
        </Button>
      </Box>
      
      {/* Выбор пола под полем ввода */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: { xs: 'center', sm: 'flex-start' }
      }}>
        {renderGenderSelector()}
      </Box>

      {error && (
        <Alert severity="error">
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default SearchComponent;
